import { useQuery } from '@tanstack/react-query';

import { getInsights } from '../../../policyholders/insights/InsightsService';
import { ActiveTab } from '../../../policyholders/insights/spotlight-details/spotlightDetails.types';
import { summariseFindings, type RiskFindings } from './riskFindings';

const EMPTY: RiskFindings = {
  total: 0,
  counted: 0,
  isComplete: true,
  bySeverity: [],
  top: [],
};

/**
 * Most severe first, so the findings we name really are the worst ones rather
 * than whichever happened to land on an unsorted first page. The tab matters
 * too: the Insights page always passes one, and without it purely
 * informational rows are counted as findings to remediate.
 */
const FINDINGS_QUERY = {
  recommendationTab: ActiveTab.INSIGHTS,
  orderBy: 'importance',
  order: 'desc',
  size: 100,
};

export interface UseRiskFindingsResult {
  readonly findings: RiskFindings;
  /** The summary could not be fetched -- distinct from having none. */
  /** The request failed. Distinct from succeeding with nothing to report. */
  readonly isUnavailable: boolean;
  /** A response came back, whatever it contained. */
  readonly isLoaded: boolean;
  readonly isLoading: boolean;
}

/**
 * The account's outstanding risk findings, from the same service the Insights
 * page reads.
 *
 * A failed fetch is reported rather than swallowed. Returning an empty summary
 * on failure would tell a policyholder they have nothing to remediate, which is
 * a claim about their account that a failed request cannot support -- and it
 * would leave them no way to ask for it again.
 */
export const useRiskFindings = (accountId?: string): UseRiskFindingsResult => {
  const query = useQuery(
    ['dashboard-risk-findings', accountId],
    () =>
      getInsights({ accountId, ...FINDINGS_QUERY }).then(({ data }: any) =>
        summariseFindings(data)
      ),
    { enabled: Boolean(accountId), refetchOnWindowFocus: false }
  );

  const findings = query.data ?? EMPTY;

  return {
    findings,
    // Only a failure. An empty response is a real answer -- an account that has
    // remediated everything has zero findings -- and reporting that as "the
    // summary didn't come back" would tell them their data is missing when it
    // is simply good news.
    isUnavailable: query.isError,
    isLoaded: query.isSuccess,
    isLoading: query.isInitialLoading,
  };
};

export default useRiskFindings;
